import { Button } from "@telegram-apps/telegram-ui";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconDelete } from "~/components/Iconfont/Iconfont";
import { useSnackbar } from "~/Provider/Snackbar";
import { fetchTokenSearchHotList, searchTokenByKeyword } from "~/api/endpoints";
import { Token } from "~/types";
import TokenList from "~/components/TokenList/TokenList";
import { formatPrice } from "~/utils/number-format";
import { TokenColumn } from "../TokenList/TokenItem";
import NoResult from "~/components/NoResult";
import useSWR from "swr";
import { debounce, isEmpty } from "lodash-es";
import FireSticker from "../LottieSticker/FireSticker";
import localStorage from "~/tools/localStorage";
import RecentSearches from "./RecentSearches";
import KeywordInput from "./KeywordInput";
import TokenProfile from "./TokenProfile";

type TokenSearchProps = {
    onTokenClick: (token: Token) => void;
    onCancel?: () => void;
};

const MAX_HISTORY_COUNT = 15;
const RECENT_HISTORY_CACHE_KEY = `__uxuy_dex_cached_recent_token_history__`;

const TokenSearch: React.FC<TokenSearchProps> = ({
    onTokenClick,
    onCancel,
}) => {
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const queryRecentHistory = useCallback(async () => {
        const result = await localStorage.get(RECENT_HISTORY_CACHE_KEY);
        return ((result || []) as string[]).slice(0, MAX_HISTORY_COUNT);
    }, []);

    const { data: historyList, mutate } = useSWR(
        "TokenSearch:HistoryList",
        queryRecentHistory,
        {
            fallbackData: [],
        }
    );

    const queryHotTokenList = useCallback(async () => {
        const res = await fetchTokenSearchHotList();
        return res.data?.list ?? [];
    }, []);

    const { data: hotTokenList, isLoading: hotTokenLoading } = useSWR(
        "TokenSearch:HotTokenList",
        queryHotTokenList,
        {
            keepPreviousData: true,
            fallbackData: [],
        }
    );

    const [keyword, setKeyword] = useState("");
    const [deferedKeyword, setDeferedKeyword] = useState("");
    const debouncedSetKeyword = useMemo(
        () =>
            debounce(
                (keyword: string) => {
                    setDeferedKeyword(keyword);
                },
                600,
                { leading: false }
            ),
        []
    );

    const queryTokenListBykeyword = useCallback(
        async ([, keyword]: [string, string]) => {
            if (isEmpty(keyword)) return [];
            return searchTokenByKeyword(keyword)
                .then((res) => {
                    const result = res.data;
                    return result.tokens ?? [];
                })
                .catch((err: any) => {
                    if ("code" in err) {
                        snackbar.error(err.message);
                    }
                    return [];
                })
                .then((result) => {
                    const newHistory = [
                        keyword,
                        ...historyList.filter((item) => item !== keyword),
                    ].slice(0, MAX_HISTORY_COUNT);
                    localStorage
                        .set(RECENT_HISTORY_CACHE_KEY, newHistory)
                        .then(() => {
                            mutate(newHistory, { revalidate: false });
                        });

                    return result;
                });
        },
        [historyList, mutate, snackbar]
    );

    const { data: tokenList, isLoading: tokenListLoading } = useSWR(
        ["TokenSearch:TokenList", deferedKeyword],
        queryTokenListBykeyword,
        {
            fallbackData: [],
        }
    );

    const isOnlyOneResult = tokenList.length === 1;

    const tokenColumns: TokenColumn[] = useMemo(
        () => [
            {
                key: "price",
                label: `${t("price")} / ${t("changes")}`,
                value(token) {
                    return formatPrice(token.token_market.price);
                },
                change(token) {
                    return token.token_market.change;
                },
            },
        ],
        [t]
    );

    const handleKeywordChange = (keyword: string) => {
        const trimedKeyword = String(keyword).trim();
        setKeyword(trimedKeyword);
        debouncedSetKeyword(trimedKeyword);
    };

    return (
        <div className="w-full h-full bg-bg_0 flex flex-col">
            <div className="flex-none px-4 flex items-center justify-between h-[52px]">
                <KeywordInput value={keyword} onChange={handleKeywordChange} />
                {onCancel ? (
                    <div className="flex-none ml-3">
                        <Button
                            size="s"
                            mode="plain"
                            style={
                                {
                                    color: "var(--color_text_brand)",
                                    padding: "8px 0",
                                    "--tgui--font_weight--accent2": "normal",
                                    "--tgui--subheadline2--font_size": "16px",
                                } as React.CSSProperties
                            }
                            onClick={onCancel}
                        >
                            {t("cancel")}
                        </Button>
                    </div>
                ) : null}
            </div>
            {isEmpty(deferedKeyword) ? (
                <div className="flex-auto relative overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 bottom-0 overflow-auto">
                        {!isEmpty(historyList) && (
                            <>
                                <div className="px-4 flex items-center justify-between h-11">
                                    <span
                                        className={
                                            "text-body1 text-text_1 font-semibold"
                                        }
                                    >
                                        {t("recent_searches")}
                                    </span>
                                    <IconDelete
                                        className={"text-body1 text-text_3"}
                                        onClick={() => {
                                            localStorage.delete(
                                                RECENT_HISTORY_CACHE_KEY
                                            );
                                            mutate([], { revalidate: false });
                                        }}
                                    />
                                </div>
                                <div className="mb-4">
                                    <RecentSearches
                                        searches={historyList}
                                        onClick={(keyword) =>
                                            handleKeywordChange(keyword)
                                        }
                                    />
                                </div>
                            </>
                        )}
                        <div className="px-4 flex items-center h-11">
                            <span
                                className={
                                    "text-body1 text-text_1 font-semibold"
                                }
                            >
                                {t("hot")}
                            </span>
                            <FireSticker />
                        </div>
                        {isEmpty(hotTokenList) && !hotTokenLoading ? (
                            <NoResult />
                        ) : (
                            <TokenList
                                isLoading={hotTokenLoading}
                                tokens={hotTokenList}
                                columns={tokenColumns}
                                onTokenClick={onTokenClick}
                                layout="Page"
                            />
                        )}
                    </div>
                </div>
            ) : isEmpty(tokenList) && !tokenListLoading ? (
                <NoResult />
            ) : isOnlyOneResult ? (
                <TokenProfile
                    token={tokenList[0]}
                    onTokenClick={onTokenClick}
                />
            ) : (
                <TokenList
                    deferredKeyword={deferedKeyword}
                    isLoading={tokenListLoading}
                    tokens={tokenList}
                    columns={tokenColumns}
                    onTokenClick={onTokenClick}
                />
            )}
        </div>
    );
};

export default TokenSearch;
